import { useEffect, useState } from "react"

/**
 * OKS-923 — the bound users are PROMISED for the refreshing marking, in ms.
 *
 * Stated in five places in the binding artifacts, all of which must move
 * together with this constant (cited by SECTION, never by line — line numbers
 * in those documents drift, and a stale cite still resolves, just to the wrong
 * paragraph):
 *
 *   - PRD R6            — "within 5 seconds" of appearing
 *   - PRD §5 F2         — the build-failable condition
 *   - PRD §7            — "5 seconds is my number and it is a ceiling, not a
 *                          target", incl. the tighten-don't-loosen rule
 *   - UX Spec §3        — "within 5 seconds of appearing"
 *   - UX Spec §9        — "staying marked past 5 seconds (F2)"
 *
 * ⚠ THIS AND {@link REFRESH_WINDOW_MS} ARE DELIBERATELY DIFFERENT NUMBERS.
 * They are different quantities that happen to share a unit: this one is the
 * ceiling we promise the user, the other is the window we actually implement,
 * and the implementation must sit strictly UNDER the promise. The gap is an
 * anti-race margin — a check polling at the ceiling must not be able to race
 * the timer.
 *
 * So do NOT "fix the drift" by making them equal. Reading `4000` next to five
 * documents saying "5 seconds" and tidying it up to `5000` deletes the margin
 * and produces a timing-flaky F2, which then gets relaxed by whoever hits it.
 * That is how the bound quietly dies. A guard spec pins the RELATIONSHIP
 * (`0 < REFRESH_WINDOW_MS < PRD_CEILING_MS`) rather than either value.
 *
 * The move-together rule binds THIS constant only. {@link REFRESH_WINDOW_MS}
 * is freely tunable underneath it. The trap direction is TIGHTENING: if a
 * measurement ever argues the ceiling down to 3s, this constant comes down
 * first — otherwise the implementation window is above the ceiling and the
 * guard reds.
 */
export const PRD_CEILING_MS = 5000

/**
 * OKS-923 — how long a section may present retained rows before the refreshing
 * marking expires on its own, in ms. Strictly under {@link PRD_CEILING_MS}
 * (see there for why the two numbers differ, and why equalising them is a
 * defect rather than a cleanup).
 */
export const REFRESH_WINDOW_MS = 4000

/**
 * What the retained rows were captured under. Any value whose change means the
 * retained rows are no longer the right ones to show — on the agency surface,
 * the acting brand id.
 */
export type RetainedIdentity = string | number | null | undefined

export interface RetainedDeferredSection<TSection> {
  /**
   * The section to render: the live prop when resolved, else the last resolved
   * one. `undefined` when there is nothing legitimate to show — first load, or
   * immediately after the identity changed — in which case the caller renders
   * its loading skeleton, which is correct because there are no rows to keep.
   */
  section: TSection | undefined
  /** Retained rows are on screen and a round-trip is in flight. */
  isRefreshing: boolean
  /** The window expired without the prop arriving — show error + Retry. */
  refreshFailed: boolean
}

/**
 * OKS-923 — retain the last resolved value of a deferred Inertia prop so the
 * `<Deferred>` fallback can render the SAME component type as the resolved
 * branch.
 *
 * # Why this exists
 *
 * `<Deferred>` renders `fallback` and `children` at one position, and resets
 * itself to the fallback on every same-URL visit. When the two branches are
 * different component types React unmounts the whole subtree — which, on the
 * triage surfaces, takes the open flag dialog and the user's typed note with
 * it, so a correctable error has nowhere to render. Feeding this hook's
 * `section` to the same component in both branches makes React reconcile
 * instead, and the dialog survives the failure round-trip.
 *
 * # The marking expires on its own — deliberately
 *
 * A hung deferred round-trip never fails by itself, so a marking that could
 * only clear when a response lands would be unbounded, and PRD §5 F2 ("still
 * marked refreshing past the ceiling") would be unenforceable. The timer is
 * armed on ENTERING the retained window and fires whether or not the data ever
 * arrives; on expiry the caller drops the marking and shows the section's
 * existing error state with Retry. F1 (diverged and silent) and F2 (marked too
 * long) therefore fail independently, as the PRD requires of the mechanism and
 * not merely of the tests.
 *
 * # Identity discard is a P0 behaviour with no visible symptom
 *
 * An agency brand switch redirects back to the SAME page, so this component
 * stays mounted while the new brand's prop resolves. Without a discard the
 * previous brand's rows would render under the new brand's header: authorised
 * content, but attributed to the wrong brand. On an identity change there is
 * nothing legitimate to keep, so the retained value is dropped and the caller
 * falls back to the skeleton.
 *
 * Note the retained value is filtered by identity on READ as well as cleared
 * on write — belt and braces, so that even a render pass React later discards
 * cannot emit rows under the wrong brand.
 *
 * ⚠ A→B→A does NOT resurrect A's rows, and that is deliberate. Leaving an
 * identity DESTROYS the retained value (`setRetained(null)` below); it does not
 * park it behind the read filter. So returning to A while the prop is still in
 * flight shows the skeleton, and `use-retained-deferred-section.test.ts`
 * (`"does not resurrect the original rows on A -> B -> A"`) pins exactly that.
 *
 * The rejected alternative — retain across the excursion, filter on read — is
 * the intuitive one, so here is why it lost, rather than leaving the next
 * reader to rediscover it. It re-shows rows with NO BOUND ON THEIR AGE: switch
 * to B, work there for five minutes, switch back, and the refreshing marking —
 * which promises "catching up", i.e. nearly current — would sit over
 * five-minute-old rows. F2 never trips, because the expiry timer re-arms each
 * time the marking appears, so every individual episode stays inside the
 * window. Bounded marking episodes, unbounded content divergence: "wrong counts
 * with an excuse", reached by a route R6's timer does not cover, which is the
 * outcome this whole ticket exists to rule out.
 *
 * The cost of dropping is a skeleton on a path that needs a brand switch AND a
 * switch back inside a single stalled sub-{@link REFRESH_WINDOW_MS} round-trip.
 * That is NOT the skeleton PRD §7 ruled against: §7 is about blanking the work
 * area mid-task, with a dialog open and typed input to preserve. After a double
 * brand switch there is no dialog and nothing typed.
 *
 * Dropping is also the structurally stronger choice for the P0 property. Rows
 * for a non-current identity are not held in state AT ALL, so no future bug in
 * the read filter can surface them; the read filter is belt-and-braces on top,
 * not the mechanism.
 *
 * ⚠ So do not "simplify" this by deleting the write-clear and relying on the
 * read filter. That reinstates the unbounded-age hole above, and no test can
 * catch the reasoning — only the behavioural pin named above.
 *
 * @param section the deferred prop — `undefined` while it resolves
 * @param identity what the rows are scoped to; a change discards them
 */
export function useRetainedDeferredSection<TSection>(
  section: TSection | undefined,
  { identity }: { identity: RetainedIdentity },
): RetainedDeferredSection<TSection> {
  const [retained, setRetained] = useState<{
    value: TSection
    identity: RetainedIdentity
  } | null>(section !== undefined ? { value: section, identity } : null)
  const [expired, setExpired] = useState(false)

  // Adjusting state during render (React's documented "adjusting state when a
  // prop changes"). It MUST be here rather than in an effect: an effect runs
  // after commit, by which point the fallback has already replaced the subtree
  // and the dialog is gone. A ref is not an option either — reading or writing
  // one during render is banned by `react-hooks/refs`, and the fallback needs
  // the value DURING the render where the prop is absent.
  if (
    section !== undefined &&
    (retained?.value !== section || retained.identity !== identity)
  ) {
    setRetained({ value: section, identity })
    setExpired(false)
  } else if (
    section === undefined &&
    retained !== null &&
    retained.identity !== identity
  ) {
    // Identity changed while the prop is in flight — the retained rows belong
    // to something the user is no longer looking at.
    setRetained(null)
    setExpired(false)
  }

  // Filtered on read too, so a discarded render pass can't emit stale-identity
  // rows even for a frame.
  const usable =
    retained !== null && retained.identity === identity ? retained : null
  const isWaiting = section === undefined && usable !== null

  useEffect(() => {
    if (!isWaiting || expired) return

    const timer = setTimeout(() => setExpired(true), REFRESH_WINDOW_MS)
    return () => clearTimeout(timer)
  }, [isWaiting, expired])

  return {
    // NB `isWaiting` rather than `section ?? usable?.value`: `??` would treat a
    // null section as absent, and "absent" here means UNDEFINED specifically —
    // the same test `<Deferred>` itself applies (`pageProps[key] !== void 0`).
    section: isWaiting ? usable?.value : section,
    isRefreshing: isWaiting && !expired,
    refreshFailed: isWaiting && expired,
  }
}
